import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ctaButtons } from '../mockData_extended';
import { Button } from './ui/button';
import { FileText, Calendar, Download } from 'lucide-react';
import { trackCTAClick, trackPDFDownload, trackViewingSchedule } from '../utils/analytics';

const CTAButtons = ({ showDownload = false, variant = 'default' }) => {
  const { language } = useLanguage();
  const content = ctaButtons[language] || ctaButtons.en;

  const scrollToContact = (action) => {
    // Track the CTA click
    if (action === 'request_details') {
      trackCTAClick('request_details');
    } else if (action === 'arrange_viewing') {
      trackCTAClick('arrange_viewing');
      trackViewingSchedule();
    }
    
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDownloadBrochure = () => {
    // Track PDF download
    trackPDFDownload();
    
    // Create a simple text-based brochure placeholder
    // In production, replace this with actual PDF generation or link to hosted PDF
    const brochureContent = `
SKOPELOS PROPERTY - TRADITIONAL COTTAGES
==========================================

PROPERTY HIGHLIGHTS:
- Two independent traditional cottage houses
- Approx. 8,000 sqm private land
- Unobstructed sea views
- Views towards Skiathos & Alonissos
- Quiet natural surroundings & privacy
- Ideal for private use or rental investment

SPECIFICATIONS:
Property Type: Two Traditional Cottages
Land Area: 8,000 m²
Location: Skopelos Island, Greece
View: Unobstructed Sea & Island Views
Privacy: Complete Privacy
Setting: Quiet Natural Surroundings
Ideal Use: Holiday Home / Investment
Islands Visible: Skiathos & Alonissos

DISTANCES:
Glossa village: 3 minutes
Loutraki port: 5 minutes
Nearest beach: 8 minutes
Skopelos town: 25 minutes

For more information and to arrange a viewing, 
please contact us through our website.

Visit: https://skopelos-property.com/
    `.trim();

    // Create blob and download
    const blob = new Blob([brochureContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Skopelos-Property-Brochure.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Hero variant uses white/glass styling for visibility against background
  const isHero = variant === 'hero';

  return (
    <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 w-full px-4 sm:px-0">
      <Button
        size="lg"
        onClick={() => scrollToContact('request_details')}
        className="w-full sm:w-auto bg-[#d4845f] hover:bg-[#c17552] text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center gap-2"
        data-testid="cta-request-details"
      >
        <FileText className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        <span className="whitespace-nowrap">{content.requestDetails}</span>
      </Button>
      
      <Button
        size="lg"
        onClick={() => scrollToContact('arrange_viewing')}
        className={`w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center gap-2 ${
          isHero 
            ? 'bg-white/20 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-[#d4845f]' 
            : 'border-2 border-[#d4845f] text-[#d4845f] hover:bg-[#d4845f] hover:text-white bg-white'
        }`}
        data-testid="cta-arrange-viewing"
      >
        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
        <span className="whitespace-nowrap">{content.arrangeViewing}</span>
      </Button>

      {showDownload && (
        <Button
          size="lg"
          onClick={handleDownloadBrochure}
          className={`w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center gap-2 ${
            isHero
              ? 'bg-white/10 backdrop-blur-md border-2 border-white/70 text-white hover:bg-white hover:text-slate-700'
              : 'border-2 border-slate-400 text-slate-700 hover:bg-slate-700 hover:text-white bg-white'
          }`}
          data-testid="cta-download-brochure"
        >
          <Download className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="whitespace-nowrap">{content.downloadBrochure}</span>
        </Button>
      )}
    </div>
  );
};

export default CTAButtons;
